import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/button';
import Tooltip from '../../ui/Tooltip';
import RewardEventSection from './RewardEventSection';
import RewardTypeSection from './RewardTypeSection';
import TimeBoundSection from './TimeBoundSection';
import SelectTierModal from './SelectTierModal';
import {
  selectCreateRewardModalOpen,
  selectRewardForm,
  closeCreateRewardModal,
  addReward,
  resetRewardForm,
  showToast,
} from '../../../store/slices/gamificationSlice';

const CreateRewardModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectCreateRewardModalOpen);
  const rewardForm = useSelector(selectRewardForm);

  // Validation logic
  const { isValid, validationMessage } = useMemo(() => {
    const messages = [];

    // Event validation
    if (!rewardForm.eventType) {
      messages.push('Select a reward event');
    } else if (rewardForm.eventType === 'CROSS_SALES' && !rewardForm.eventConfig.salesAmount) {
      messages.push('Enter sales target amount');
    } else if (rewardForm.eventType === 'POST_FREQUENCY') {
      if (!rewardForm.eventConfig.postCount) {
        messages.push('Enter post count');
      } else if (!rewardForm.eventConfig.duration) {
        messages.push('Select duration');
      }
    }

    // Reward validation
    if (!rewardForm.rewardType) {
      messages.push('Select a reward type');
    } else if (rewardForm.rewardType === 'FLAT_BONUS' && !rewardForm.rewardConfig.bonusAmount) {
      messages.push('Enter bonus amount');
    } else if (rewardForm.rewardType === 'COMMISSION_TIER' && !rewardForm.rewardConfig.tierId) {
      messages.push('Select commission tier');
    }

    // Time bound validation
    if (rewardForm.isTimeBound && !rewardForm.endDate) {
      messages.push('Select end date');
    }

    return {
      isValid: messages.length === 0,
      validationMessage: messages.join(', '),
    };
  }, [rewardForm]);

  const handleClose = () => {
    dispatch(closeCreateRewardModal());
    dispatch(resetRewardForm());
  };

  const handleCreateReward = () => {
    if (!isValid) return;

    const reward = {
      eventType: rewardForm.eventType,
      eventConfig: { ...rewardForm.eventConfig },
      rewardType: rewardForm.rewardType,
      rewardConfig: { ...rewardForm.rewardConfig },
      isTimeBound: rewardForm.isTimeBound,
      endDate: rewardForm.endDate,
    };

    dispatch(addReward(reward));
    dispatch(showToast('Reward Created!'));
    dispatch(closeCreateRewardModal());
    dispatch(resetRewardForm());
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Create your reward system"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Tooltip
              message={validationMessage}
              show={!isValid}
              position="top"
              hoverOnly={true}
            >
              <Button onClick={handleCreateReward} disabled={!isValid}>
                Create Reward
              </Button>
            </Tooltip>
          </>
        }
      >
        <div className="space-y-5">
          <RewardEventSection />
          <RewardTypeSection />
          <TimeBoundSection />
        </div>
      </Modal>

      {/* Nested Tier Selection Modal */}
      <SelectTierModal />
    </>
  );
};

export default CreateRewardModal;
